import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import productService from 'api/productService';

export const useAllProducts = (filterParams) => {
    return useQuery({
        queryKey: ['allProducts', filterParams],
        queryFn: () => productService.allProducts(filterParams),
        staleTime: 5 * 60 * 1000,
        refetchInterval: 2 * 60 * 1000
    });
};

export const useProductDetails = (productId) => {
    return useQuery({
        queryKey: ['productDetails', productId],
        queryFn: () => productService.getProductDetails(productId),
        enabled: !!productId,
        staleTime: 5 * 60 * 1000,
        refetchInterval: 2 * 60 * 1000
    });
};

export const useUpdateProduct = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => productService.updateProduct(id, payload),

        // 1. When mutate is called:
        onMutate: async ({ id, payload }) => {
            // Cancel any outgoing refetches so they don't overwrite our optimistic update
            await queryClient.cancelQueries({ queryKey: ['productDetails', id] });

            // Snapshot the previous value
            const previousProduct = queryClient.getQueryData(['productDetails', id]);

            // Optimistically update the cache with the new data
            queryClient.setQueryData(['productDetails', id], (old) => ({
                ...old,
                ...payload // This spreads the updated fields (like stock or isAvailable)
            }));

            // Return a context object with the snapshotted value
            return { previousProduct, id };
        },

        // 2. If the mutation fails, use the context returned from onMutate to roll back
        onError: (err, variables, context) => {
            queryClient.setQueryData(['productDetails', context.id], context.previousProduct);
        },

        // 3. Always refetch after error or success to ensure we are in sync with the server
        onSettled: (data, error, variables) => {
            queryClient.invalidateQueries({ queryKey: ['productDetails', variables.id] });
            queryClient.invalidateQueries({ queryKey: ['allProducts'] });
        }
    });
};

export const useDeleteProduct = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (productId) => productService.deleteProduct(productId),
        onSuccess: (_, productId) => {
            // Refresh product list after deletion
            queryClient.invalidateQueries(['allProducts']);
            // specifically remove this product from the details cache
            queryClient.removeQueries(['productDetails', productId]);
        }
    });
};

export const useCreateProduct = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (newProduct) => productService.createProduct(newProduct),
        onSuccess: () => {
            // Refresh product list after creating
            queryClient.invalidateQueries(['allProducts']);
        }
    });
};
